import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DEFAULT_THEME_ID, getThemeById, type ThemeConfig } from '../constants/themes';

interface ThemeState {
    themeId: string;
    theme: ThemeConfig;
    isSelectorOpen: boolean;

    setTheme: (themeId: string) => void;
    openSelector: () => void;
    closeSelector: () => void;
}

export const useThemeStore = create<ThemeState>()(
    persist(
        (set) => ({
            themeId: DEFAULT_THEME_ID,
            theme: getThemeById(DEFAULT_THEME_ID),
            isSelectorOpen: false,

            setTheme: (themeId: string) => {
                const theme = getThemeById(themeId);
                set({ themeId, theme });
                applyThemeToDocument(theme);
            },

            openSelector: () => set({ isSelectorOpen: true }),
            closeSelector: () => set({ isSelectorOpen: false }),
        }),
        {
            name: 'lunar-calendar-theme',
            partialize: (state) => ({ themeId: state.themeId }),
            onRehydrateStorage: () => (state) => {
                if (state?.themeId) {
                    const theme = getThemeById(state.themeId);
                    state.theme = theme;
                    applyThemeToDocument(theme);
                }
            },
        }
    )
);

function applyThemeToDocument(theme: ThemeConfig) {
    const root = document.documentElement;
    root.style.setProperty('--color-primary', theme.colors.primary);
    root.style.setProperty('--color-secondary', theme.colors.secondary);
    root.style.setProperty('--color-accent', theme.colors.accent);
    root.style.setProperty('--color-background', theme.colors.background);
    root.style.setProperty('--color-surface', theme.colors.surface);
    root.style.setProperty('--color-text', theme.colors.text);
    root.style.setProperty('--color-text-muted', theme.colors.textMuted);
    root.style.setProperty('--gradient-from', theme.gradient.from);
    root.style.setProperty('--gradient-via', theme.gradient.via);
    root.style.setProperty('--gradient-to', theme.gradient.to);
}

export default useThemeStore;
